/**
 * Header Component
 *
 * Top navigation bar component that displays:
 * - Application branding (logo and title)
 * - User profile information with dropdown menu
 * - User role badge
 * - Logout functionality
 * - Mobile hamburger menu button
 *
 * This component is visible only when the user is authenticated.
 *
 * @example
 * ```html
 * <app-header></app-header>
 * ```
 */

import { Component, OnInit, OnDestroy, computed, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MenuItem } from 'primeng/api';

import { AuthService } from '../../../../core/services/auth.service';
import { UserService } from '../../../../core/services/user.service';
import { ViewModeService, ViewMode } from '../../../../core/services/view-mode.service';
import { LanguageService, Language } from '../../../../core/services/language.service';
import { TranslationService } from '../../../../core/services/translation.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { User } from '../../../../core/models';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AvatarModule,
    MenuModule,
    SelectButtonModule,
    TranslatePipe
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  /**
   * Current authenticated user
   */
  currentUser: User | null = null;

  /**
   * User profile menu items
   */
  userMenuItems: MenuItem[] = [];

  /**
   * Current view mode value bound to SelectButton
   */
  currentViewMode: ViewMode = 'default';

  /**
   * Options for the view mode SelectButton
   */
  viewModeOptions: { label: string; value: ViewMode; icon: string }[] = [];

  private translationService = inject(TranslationService);

  /**
   * Language service for i18n
   */
  public languageService = inject(LanguageService);

  /**
   * Display label for the language toggle button
   */
  public readonly languageDisplay = computed(() => {
    return this.languageService.currentLanguage() === 'en' ? 'EN' : 'ع';
  });

  /**
   * Subject for managing component subscriptions
   */
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private viewModeService: ViewModeService,
    private router: Router
  ) {
    effect(() => {
      this.translationService.currentLanguage();
      this.translationService.translationsLoaded();
      this.viewModeOptions = [
        { label: this.translationService.translate('header.viewOverview'), value: 'branch' as ViewMode, icon: 'pi pi-sitemap' },
        { label: this.translationService.translate('header.viewSchools'), value: 'default' as ViewMode, icon: 'pi pi-building' }
      ];
    });
  }

  /**
   * Initializes the component
   * Subscribes to current user and sets up menu items
   */
  ngOnInit(): void {
    // Subscribe to current user changes
    this.userService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.setupUserMenu();
      });

    // Subscribe to view mode changes to keep toggle in sync
    this.viewModeService.viewMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.currentViewMode = mode;
      });
  }

  /**
   * Cleanup when component is destroyed
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up the user profile dropdown menu items
   */
  private setupUserMenu(): void {
    this.userMenuItems = [
      {
        label: 'Profile',
        icon: 'pi pi-user',
        command: () => {
          this.router.navigate(['/profile']);
        }
      },
      {
        separator: true
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => {
          this.logout();
        }
      }
    ];
  }

  /**
   * Handles user logout
   * Calls auth service to logout and redirects to login page
   */
  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/auth/login']);
      },
      error: (error) => {
        console.error('Logout failed:', error);
        // Even if logout fails on server, clear local state and redirect
        this.router.navigate(['/auth/login']);
      }
    });
  }

  /**
   * Handles view mode toggle change
   * Updates the service and navigates to the appropriate home route
   */
  onViewModeChange(mode: ViewMode): void {
    if (!mode) return;
    // Navigate first, then update view mode so the sidebar rebuilds
    // AFTER the URL has changed — this ensures RouterLinkActive
    // correctly highlights the active menu item.
    const route = mode === 'branch' ? '/branch-view/dashboard' : '/dashboard';
    this.router.navigate([route]).then(() => {
      this.viewModeService.setViewMode(mode);
    });
  }

  /**
   * Gets the user's initials for avatar display
   *
   * @returns User initials (e.g., "JD" for "John Doe")
   */
  getUserInitials(): string {
    if (!this.currentUser?.name) {
      return 'U';
    }
    const names = this.currentUser.name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[1][0]).toUpperCase();
    }
    return this.currentUser.name.substring(0, 2).toUpperCase();
  }

  /**
   * Toggles between English and Arabic
   */
  toggleLanguage(): void {
    const current = this.languageService.currentLanguage();
    const newLang: Language = current === 'en' ? 'ar' : 'en';
    this.languageService.setLanguage(newLang);
  }

  /**
   * Gets the CSS class for the role badge
   *
   * @returns CSS class based on user role
   */
  getRoleBadgeClass(): string {
    if (this.userService.isSuperAdmin()) {
      return 'role-badge role-badge-super-admin';
    }
    return 'role-badge role-badge-admin';
  }

  /**
   * Gets the display text for the user's role
   *
   * @returns Formatted role name
   */
  getRoleDisplayName(): string {
    if (!this.currentUser?.role) {
      return '';
    }
    if (this.currentUser.role.name === 'comp_superadmin') {
      return this.translationService.translate('roles.superAdmin');
    }
    if (this.currentUser.role.name === 'comp_admin') {
      return this.translationService.translate('roles.admin');
    }
    return this.currentUser.role.name;
  }
}
